import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { fmtNum, fmtMoney } from "../lib/format";
import DataTable from "../components/DataTable";
import EntityFormDialog from "../components/EntityFormDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function Masters() {
  const { data: entities = [] } = useQuery({ queryKey: ["entities"], queryFn: () => api.get("/entities") });
  const { data: commodities = [] } = useQuery({ queryKey: ["commodities"], queryFn: () => api.get("/commodities") });
  const { data: fx = [] } = useQuery({ queryKey: ["fx"], queryFn: () => api.get("/fx-rates") });
  const { data: futures = [] } = useQuery({ queryKey: ["futures"], queryFn: () => api.get("/futures-prices") });
  const { data: banks = [] } = useQuery({ queryKey: ["banks"], queryFn: () => api.get("/banks") });
  const { data: counterparties = [] } = useQuery({ queryKey: ["cps"], queryFn: () => api.get("/counterparties") });
  const [fxForm, setFxForm] = useState(false);
  const [futForm, setFutForm] = useState(false);

  const fxFields = [
    { key: "base", label: "Base Currency", type: "text", default: "USD", required: true },
    { key: "quote", label: "Quote Currency", type: "text", required: true },
    { key: "rate", label: "Rate", type: "number", required: true },
    { key: "source", label: "Source", type: "select", options: ["Manual", "API"], default: "Manual" },
  ];
  const futFields = [
    { key: "exchange", label: "Exchange", type: "select", options: ["BMD", "CBOT", "MCX", "NCDEX", "ICE", "DCE"], required: true },
    { key: "symbol", label: "Symbol", type: "text", required: true },
    { key: "contract_month", label: "Contract Month", type: "text", required: true },
    { key: "price", label: "Price", type: "number", required: true },
    { key: "currency", label: "Currency", type: "text", default: "USD" },
  ];

  const entCols = [
    { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs font-semibold">{r.code}</span> },
    { key: "name", label: "Entity" }, { key: "country", label: "Country" },
    { key: "base_currency", label: "Base Ccy" }, { key: "type", label: "Type" },
  ];
  const commCols = [
    { key: "name", label: "Symbol", render: (r) => <span className="font-semibold uppercase text-xs">{r.name}</span> },
    { key: "full_name", label: "Full Name" }, { key: "category", label: "Category" },
    { key: "uom", label: "UOM" }, { key: "futures_ref", label: "Futures Ref" },
  ];
  const fxCols = [
    { key: "base", label: "Base" }, { key: "quote", label: "Quote" },
    { key: "rate", label: "Rate", align: "right", render: (r) => fmtNum(r.rate, 4) },
    { key: "source", label: "Source" },
  ];
  const futCols = [
    { key: "exchange", label: "Exchange" }, { key: "symbol", label: "Symbol" },
    { key: "contract_month", label: "Month" },
    { key: "price", label: "Price", align: "right", render: (r) => fmtNum(r.price, 2) },
    { key: "currency", label: "Currency" },
  ];
  const bankCols = [
    { key: "name", label: "Bank" }, { key: "country", label: "Country" }, { key: "swift", label: "SWIFT" },
    { key: "facility_limit", label: "Facility Limit", align: "right", render: (r) => fmtMoney(r.facility_limit) },
  ];
  const cpCols = [
    { key: "name", label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "type", label: "Type" }, { key: "country", label: "Country" }, { key: "rating", label: "Rating" },
    { key: "credit_limit", label: "Credit Limit", align: "right", render: (r) => fmtMoney(r.credit_limit) },
    { key: "kyc_status", label: "KYC" },
  ];

  return (
    <div data-testid="masters-page">
      <Tabs defaultValue="entities">
        <TabsList className="mb-3 flex-wrap h-auto">
          <TabsTrigger value="entities" data-testid="tab-entities">Entities</TabsTrigger>
          <TabsTrigger value="counterparties" data-testid="tab-cp">Counterparties</TabsTrigger>
          <TabsTrigger value="commodities" data-testid="tab-commodities">Commodities</TabsTrigger>
          <TabsTrigger value="fx" data-testid="tab-fx">FX Rates</TabsTrigger>
          <TabsTrigger value="futures" data-testid="tab-futures">Futures Prices</TabsTrigger>
          <TabsTrigger value="banks" data-testid="tab-banks">Banks</TabsTrigger>
        </TabsList>
        <TabsContent value="entities"><DataTable testid="entities-table" title="Legal Entities" columns={entCols} rows={entities} csvName="entities" /></TabsContent>
        <TabsContent value="counterparties"><DataTable testid="cp-master-table" title="Counterparty Master" columns={cpCols} rows={counterparties} csvName="counterparties" /></TabsContent>
        <TabsContent value="commodities"><DataTable testid="commodities-table" title="Commodity Master" columns={commCols} rows={commodities} csvName="commodities" /></TabsContent>
        <TabsContent value="fx"><DataTable testid="fx-table" title="FX Rate Table" columns={fxCols} rows={fx} csvName="fx_rates" onAdd={() => setFxForm(true)} addLabel="New Rate" /></TabsContent>
        <TabsContent value="futures"><DataTable testid="futures-table" title="Futures Reference Prices" columns={futCols} rows={futures} csvName="futures_prices" onAdd={() => setFutForm(true)} addLabel="New Price" /></TabsContent>
        <TabsContent value="banks"><DataTable testid="banks-table" title="Bank Master" columns={bankCols} rows={banks} csvName="banks" /></TabsContent>
      </Tabs>
      <EntityFormDialog open={fxForm} onClose={() => setFxForm(false)} title="New FX Rate" path="fx-rates" fields={fxFields} testid="fx-form" transform={(p) => ({ ...p, as_of: new Date().toISOString() })} />
      <EntityFormDialog open={futForm} onClose={() => setFutForm(false)} title="New Futures Price" path="futures-prices" fields={futFields} testid="fut-form" transform={(p) => ({ ...p, as_of: new Date().toISOString() })} />
    </div>
  );
}
